import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
// import { getProductsByType } from "../../../../../redux/features/products/productSlice";
import { debounce } from "lodash";
// import NoDataFoundTemplete from "../../../../../components/AGGridTable/NoDataFoundTemplete";
import {
  Box,
  TextField,
  ListItem,
  ListItemButton,
  Checkbox,
  ListItemText,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { _productsList } from "./data";
// import { FixedSizeList } from "react-window";

import { useContext } from "react";
import { PlatformContext } from "./AddPromotions";
import { getPlatformProducts } from "../../redux/slices/promotions";
import {
  fetchAllListingItems,
  mergeListingSelection,
  removeListingSelectionByIds,
  isListingSelectAllChecked,
  shouldClearListingSelectAll,
  PROMO_SELECT_ALL_MAX,
} from "./promoListingSelectAll";

/**
 * EcomAndCatalogProduct component fetches and displays a list of products
 * from the e-commerce and catalog based on the specified filter type.
 *
 * @param {Function} changeHandler - Callback function to handle product selection.
 * @param {Array} filterType - Array of strings specifying the types of products to filter.
 * @param {Object} inputValue - Object containing the currently selected product details.
 */
export default function EcomAndCatalogProduct({
  changeHandler = () => {},
  // filterType = ["ecommerce"],
  inputValue = {},
  isShowAnyOption = false,
  listType = "",
  setAnchorEl,
}) {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    skip: 0,
    limit: 10,
    // type: filterType,
    searchTerm: "",
  });
  const platform = useContext(PlatformContext);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [productsList, setProductsList] = useState([]);
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [selectAllScopeIds, setSelectAllScopeIds] = useState([]);

  const observer = useRef();

  const mapProductItem = (item) => ({
    productId: item?.productId,
    display_name: item?.productName,
    brand: item?.brandName,
    is_sale_item: item?.isSalePrice,
    sale_price: item?.salePrice,
    type: item?.categoryName,
    SKU: item?.sku,
    productSlug: item?.productSlug,
  });

  const lastPostElementRef = useCallback(
    (node) => {
      if (isLoading || productsList.length >= total) return;
      if (observer.current) observer.current.disconnect();
      const { skip, limit, searchTerm } = filter;

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            const newSkip = skip + limit;
            setIsLoading(true);

            dispatch(
              getPlatformProducts({
                skip: newSkip,
                limit,
                platform: platform,
                searchTerm: searchTerm || "",
              })
            )
              .unwrap()
              .then((res) => {
                const { data, total: newTotal } = res;
                const newProducts = data?.map(mapProductItem);
                setProductsList((prev) => [...prev, ...newProducts]);
                setTotal(newTotal);
                setFilter((prev) => ({
                  ...prev,
                  skip: newSkip,
                }));
              })
              .finally(() => setIsLoading(false));
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, productsList.length, total, filter, platform, dispatch]
  );

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getPlatformProducts({
        skip: filter?.skip,
        limit: filter?.limit,
        platform: platform,
        searchTerm: filter?.searchTerm || "",
      })
    )
      .unwrap()
      .then((res) => {
        setIsLoading(false);

        const { data, total } = res;
        const productData = data?.map(mapProductItem);
        setTotal(total);
        setProductsList(productData);
        setSelectAllActive(false);
        setSelectAllScopeIds([]);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [platform]);

  const listingSelectAllParams = {
    platform,
    searchTerm: filter.searchTerm || "",
  };

  const isAllSelected = isListingSelectAllChecked({
    inputValue,
    visibleItems: productsList,
    idKey: "productId",
    scopeIds: selectAllScopeIds,
    selectAllActive,
    total,
  });

  const handleSelectAll = async () => {
    if (!Array.isArray(inputValue)) return;

    if (
      shouldClearListingSelectAll({
        inputValue,
        visibleItems: productsList,
        idKey: "productId",
        scopeIds: selectAllScopeIds,
        selectAllActive,
        total,
      })
    ) {
      setSelectAllLoading(true);
      try {
        let idsToRemove = selectAllScopeIds;
        if (!idsToRemove.length) {
          const { items } = await fetchAllListingItems({
            dispatch,
            fetchThunk: getPlatformProducts,
            params: listingSelectAllParams,
            mapItem: mapProductItem,
            pageSize: filter.limit,
          });
          idsToRemove = items.map((p) => p.productId).filter(Boolean);
        }
        changeHandler({
          ruleValue: removeListingSelectionByIds(inputValue, idsToRemove, "productId"),
        });
        setSelectAllActive(false);
        setSelectAllScopeIds([]);
      } catch {
        // keep prior selection on failure
      } finally {
        setSelectAllLoading(false);
      }
      return;
    }

    setSelectAllLoading(true);
    try {
      const { items, total: apiTotal, capped } = await fetchAllListingItems({
        dispatch,
        fetchThunk: getPlatformProducts,
        params: listingSelectAllParams,
        mapItem: mapProductItem,
        pageSize: filter.limit,
      });

      const ids = items.map((p) => p.productId).filter(Boolean);
      setSelectAllScopeIds(ids);
      changeHandler({
        ruleValue: mergeListingSelection(inputValue, items, "productId"),
      });
      setSelectAllActive(true);
      if (capped && typeof console !== "undefined") {
        console.warn(
          `Select all capped at ${PROMO_SELECT_ALL_MAX} of ${apiTotal} products. Narrow your search if needed.`
        );
      }
    } catch {
      // keep prior selection on failure
    } finally {
      setSelectAllLoading(false);
    }
  };

  // useEffect(() => {
  //   setIsLoading(true);
  //   if (platform === "hyperwolf") {
  //     dispatch(
  //       getAllProducts({
  //         limit: filter?.limit,
  //       })
  //     )
  //       .unwrap()
  //       .then((res) => {
  //         setIsLoading(false);

  //         const { values, total } = res;
  //         const productData = values?.map((item) => {
  //           return {
  //             id: item?._id,
  //             display_name: item?.brandName,
  //             name: item?.brandName,
  //             is_sale_item: true,
  //             sale_price: item?.salePrice,
  //             type: item?.categoryName,
  //             SKU: item?.sku,
  //           };
  //         });
  //         setTotal(total);
  //         setProductsList(productData);
  //       });
  //   }
  // else if (platform === "hemp") {
  //   dispatch(fetchProducts({filters: { skip: filter.skip, limit: 100 }}))
  //     .unwrap()
  //     .then((res) => {
  //       const { filteredProducts, total } = res;
  //       const productData = filteredProducts?.map((item) => {
  //         return {
  //           id: item?._id,
  //           display_name: item?.productName,
  //           name: item?.productName,
  //           is_sale_item: true,
  //           sale_price: item?.salePrice,
  //           type: item?.categoryName,
  //           SKU: item?.sku,
  //         };
  //       });
  //       setTotal(total);
  //       setProductsList(productData);
  //     });
  // }
  // }, []);

  const _findCommonProducts = (inputValue, productRef) => {
    if (!Array.isArray(inputValue)) return [];
    if (!Array.isArray(productRef)) return [];

    return productRef.filter((product) =>
      inputValue.some((selectedProduct) => selectedProduct.productId === product.productId)
    );
  };
  const Row = ({ index, style }) => {
    const product = {
      ...productsList[index],
      display_name: productsList[index]?.display_name || productsList[index]?.name || "",
    };

    const isChecked = Array.isArray(inputValue)
      ? inputValue.some((item) => item?.productId === product?.productId)
      : inputValue?.productId === product?.productId;


    return (
      <ListItem
        key={product?.productId}
        disablePadding
        style={style}
        ref={productsList.length === index + 1 ? lastPostElementRef : null}
      >
        <ListItemButton
          onClick={() => {
            if (Array.isArray(inputValue)) {
              if (!isChecked) {
                changeHandler({ ruleValue: [...inputValue, product] });
              } else {
                changeHandler({
                  ruleValue: inputValue.filter((item) => item?.productId !== product?.productId),
                });
              }
            } else if (isShowAnyOption && (listType === "product" || listType === "free_product" || listType === "free_bogo_product")) {
              changeHandler({ ruleValue: [product] });
            } else {
              setAnchorEl(null);
              changeHandler({
                ruleValue: isChecked ? inputValue : product,
              });
            }
          }}
          sx={{
            p: 0,
            bgcolor: (_theme) => (isChecked ? "action.selected" : "inherit"),
          }}
          className="popover-btn-area bg-transparent"
        >
          {(Array.isArray(inputValue) ||
            (isShowAnyOption && (listType === "product" || listType === "free_product" || listType === "free_bogo_product"))) && (
            <Checkbox
              edge="start"
              checked={isChecked}
              tabIndex={-1}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                if (e.target.checked) {
                  if (!isChecked) {
                    changeHandler({ ruleValue: [...inputValue, product] });
                  }
                } else {
                  changeHandler({
                    ruleValue: inputValue.filter((item) => item?.productId !== product?.productId),
                  });
                }
              }}
              style={{ margin: 0 }}
            />
          )}

          <ListItemText>
            <Typography variant="body2" color="#2E2E3A" fontWeight="semibold">
              {product.display_name}
            </Typography>
            <Typography variant="body2" color="#2E2E3A">{`$${
              product?.sale_price || 0
            }`}</Typography>
          </ListItemText>
          {/* <ListItemText
            primary={product.display_name}
            secondary={`$${product?.sale_price || 0}`}
            sx={{
              color: (theme) => (isChecked ? theme.palette.primary.main : "inherit"),
            }}
          /> */}

          {product?.is_sale_item && (
            <Chip
              label="Sale"
              size="small"
              sx={{
                ml: 1,
                bgcolor: "success.main",
                color: "white",
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Search Box */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search products..."
          onChange={debounce((e) => {
            const searchValue = e.target.value;
            setIsLoading(true);
            setFilter((prev) => ({ ...prev, searchTerm: searchValue, skip: 0 }));
            setSelectAllActive(false);
            setSelectAllScopeIds([]);

            dispatch(
              getPlatformProducts({
                skip: 0,
                limit: filter?.limit,
                platform: platform,
                searchTerm: searchValue || "",
              })
            )
              .unwrap()
              .then((res) => {
                setIsLoading(false);
                const { data, total } = res;
                const productData = data?.map(mapProductItem);
                setTotal(total);
                setProductsList(productData);
              })
              .catch(() => {
                setIsLoading(false);
              });
          }, 500)}
        />
      </Box>

      {/* Loading State */}

      {/* Products List */}
      {/* {productsList?.length > 0 && (
        <FixedSizeList
          sx={{ maxHeight: 400, overflow: "auto" }}
          height={400}
          itemCount={productsList?.length}
          itemSize={65}
          width={400}
        >
          {Row}
        </FixedSizeList>
      )} */}
     
     
      {/* Empty State */}
      {!isLoading && productsList?.length === 0 && (
        <Box sx={{ p: 3, transform: "scale(0.9)" }}>
          {/* <NoDataFoundTemplete imgStyle={{ height: 50 }} /> */}
          Data Not Found
        </Box>
      )}
      {Array.isArray(inputValue) && productsList?.length !== 0 && (
        <ListItem
          disablePadding
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          <ListItemButton onClick={handleSelectAll} className="p-0" disabled={selectAllLoading}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                !selectAllLoading &&
                Array.isArray(inputValue) &&
                inputValue.length > 0 &&
                !isAllSelected
              }
              disabled={selectAllLoading}
              tabIndex={-1}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
            />
            <ListItemText
              primary={
                <Typography fontWeight={600}>
                  {selectAllLoading
                    ? "Selecting all…"
                    : total > 0
                      ? `Select all (${total})`
                      : "Select all"}
                </Typography>
              }
              secondary={
                total > PROMO_SELECT_ALL_MAX ? (
                  <Typography variant="caption" color="text.secondary">
                    Max {PROMO_SELECT_ALL_MAX} per rule
                  </Typography>
                ) : null
              }
            />
          </ListItemButton>
        </ListItem>
      )}
      

      <Box sx={{ maxHeight: 300, overflow: "auto" }}>
        {productsList.map((_, index) => (
          <Row key={productsList[index]?.productId} index={index} />
        ))}
      </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} color="primary" />
          </Box>
        )}{" "}
    </Box>

    
  );
}
